import React, { useState, useEffect, useContext } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';

import RunStatusList from './Runs/RunStatusList';
import AddSamplesDialog from './Samples/AddSamplesDialog';
import AddRunDialog from './Runs/AddRunDialog';

import DatasetsDataGrid from './Datasets/DatasetsDataGrid.jsx';
import { useDatasets } from './services/api/KeynomeAPIHooks.js';
import { ProjectContext, ProjectDispatchContext } from './context/ProjectContext.jsx';

function Project() {
  const {
    datasetsGridRef,
    uploadFile,
    uploadingFilesCt,
    userData,
    setUserErrorMessage
  } = useOutletContext();
  const params = useParams();

  const { activeProject, recentlyViewedDatasets } = useContext(ProjectContext);
  const { addRecentlyViewedDataset } = useContext(ProjectDispatchContext);

  const [ activeDataset, setActiveDataset ] = useState(null);
  const [ addDatasetDialogOpen, setAddDatasetDialogOpen ] = useState(false);
  const [ addSamplesDialogOpen, setAddSamplesDialogOpen ] = useState(false);

  const { data: datasets, refetch: refetchDatasets } = useDatasets(activeProject?.id, { queryParams: { extraFields: ['keynome_web_url'] } });

  useEffect(() => {
    if (!datasets) return;

    const dataset = datasets.find((dataset) => dataset.attributes.uuid === params.runId);
    setActiveDataset(dataset || null);

    if (dataset) setAddSamplesDialogOpen(true);
  }, [datasets, params.runId]);

  /*
  * On each new activeRun change, add that active dataset to the list of visible runs
  * (Recently Viewed Datasets).
  */
  useEffect(() => {
    if (!activeDataset) return;
    addRecentlyViewedDataset(activeDataset);
  }, [activeDataset, addRecentlyViewedDataset]);


  const recentlyViewedDatasetsContent = recentlyViewedDatasets ? 
    (
      <RunStatusList
        runs={recentlyViewedDatasets}
        activeRun={activeDataset}
        setActiveRun={setActiveDataset}
        setAddSamplesDialogOpen={setAddSamplesDialogOpen}
      />
    ) : null;

  const createNewDatasetButton = activeDataset ?
    null : (
      <Button
        size="large"
        sx={{ marginRight: '10px', marginTop: '30px' }}
        variant="contained"
        color="info"
        aria-label="create-dataset"
        data-cy="create-new-dataset"
        onClick={() => setAddDatasetDialogOpen(true)}
        startIcon={<AddIcon />}>
        Create New Dataset
      </Button>
    )      

  return (
    <Grid container maxWidth="lg" spacing={2} sx={{ m: 'auto', mt: 1, paddingRight: '30px' }}>
      <Grid item xs={12} sx={{marginTop: '50px'}}>
        <p style={{color: '#797979', marginBottom: '2px', marginTop: '2px'}}>
          <b>RECENTLY VIEWED DATASETS</b>
        </p>
      </Grid>
      {recentlyViewedDatasetsContent}
      <Grid item xs={12} sm={8} sx={{ marginTop: '50px'}}>
        <p style={{color: '#797979', marginBottom: '2px', marginTop: '2px'}}>
          <b>SEQUENCING DATASETS</b>
        </p>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box sx={{ textAlign: 'right'}}>
          {createNewDatasetButton}
        </Box>
      </Grid>
      <Grid 
        item 
        xs={12} 
        height="665px" 
        sx={{
          '& .keynome-table--header': { backgroundColor: "#eee" },
          marginBottom: '40px'
        }}
      >
        <DatasetsDataGrid
          datasets={datasets}
          datasetsGridRef={datasetsGridRef}
          setAddSamplesDialogOpen={setAddSamplesDialogOpen}
        />
      </Grid>
      <AddSamplesDialog 
        key={activeProject?.id}
        open={addSamplesDialogOpen} 
        setOpen={setAddSamplesDialogOpen} 
        activeRun={activeDataset} 
        uploadFile={uploadFile} 
        backgroundUploadingFilesCt={uploadingFilesCt}  
        activeProject={activeProject} 
        setReloadRuns={refetchDatasets}
        setUpdateRunDialogOpen={setAddDatasetDialogOpen} 
      />
      <AddRunDialog 
        open={addDatasetDialogOpen} 
        setOpen={setAddDatasetDialogOpen} 
        activeProject={activeProject} 
        userData={userData} 
        reloadRuns={refetchDatasets} 
        setUserErrorMessage={setUserErrorMessage}
        activeDataset={activeDataset}
      />
    </Grid>
  );
}

export default Project;
